import {apiSlice} from './apiSlice';

/**
 * Endpoints tool Phân tích đối thủ (#21, bên trong một Project — docs/features/competitor.md).
 *
 * - getCompetitors: danh sách đối thủ + snapshot mới nhất từng đối thủ/own site + allowed
 *   (gói không có tính năng → allowed=false, FE hiện CTA nâng cấp) + used/max.
 * - analyzeCompetitor/analyzeOwnSite: chấm điểm SEO on-page 1 domain (cache 24h phía BE
 *   miễn phí; live tiêu 1 lượt url_checks → invalidate 'Subscription').
 * - getCompetitorCompare/getCompetitorReport: bảng so sánh + chi tiết drill-down (DB thuần).
 * - competitorContentGap: so trang mình với NHIỀU trang đối thủ (ghi chung
 *   keyword_page_terms với tool Keyword → invalidate tag pages của Keyword).
 * - getCompetitorCoverage: ma trận độ phủ từ khoá GSC × đối thủ (DB thuần, không quota).
 *
 * Tag: 'Competitor' (list/compare/coverage theo project, report theo id).
 */
export const competitorApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getCompetitors: builder.query({
			query: (projectId) => ({url: `competitor/projects/${projectId}`, method: 'get'}),
			transformResponse: (body) => body?.data || {allowed: false, competitors: [], used: 0, max: 0, own: null},
			providesTags: (result, error, projectId) => [{type: 'Competitor', id: `list-${projectId}`}],
		}),
		addCompetitor: builder.mutation({
			query: ({projectId, url, name, note}) => ({
				url: `competitor/projects/${projectId}`,
				method: 'post',
				data: {url, ...(name ? {name} : {}), ...(note ? {note} : {})},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `list-${projectId}`},
				{type: 'Competitor', id: `compare-${projectId}`},
				{type: 'Competitor', id: `coverage-${projectId}`},
			],
		}),
		updateCompetitor: builder.mutation({
			query: ({projectId, id, name, note, url}) => ({
				url: `competitor/projects/${projectId}/${id}`,
				method: 'put',
				data: {
					...(name !== undefined ? {name} : {}),
					...(note !== undefined ? {note} : {}),
					...(url !== undefined ? {url} : {}),
				},
			}),
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `list-${projectId}`},
				{type: 'Competitor', id: `compare-${projectId}`},
			],
		}),
		deleteCompetitor: builder.mutation({
			query: ({projectId, id}) => ({url: `competitor/projects/${projectId}/${id}`, method: 'delete'}),
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `list-${projectId}`},
				{type: 'Competitor', id: `compare-${projectId}`},
				{type: 'Competitor', id: `coverage-${projectId}`},
			],
		}),
		// Chấm điểm 1 đối thủ (refresh=1 bỏ qua cache 24h — tốn 1 lượt url_checks).
		analyzeCompetitor: builder.mutation({
			query: ({projectId, id, refresh}) => ({
				url: `competitor/projects/${projectId}/${id}/analyze`,
				method: 'post',
				...(refresh ? {data: {refresh: 1}} : {}),
			}),
			transformResponse: (body) => body?.data || null,
			// Tiêu url_checks → 'Subscription' cập nhật đồng hồ hạn mức.
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `list-${projectId}`},
				{type: 'Competitor', id: `compare-${projectId}`},
				'Subscription',
			],
		}),
		analyzeOwnSite: builder.mutation({
			query: ({projectId, refresh}) => ({
				url: `competitor/projects/${projectId}/analyze-own`,
				method: 'post',
				...(refresh ? {data: {refresh: 1}} : {}),
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `list-${projectId}`},
				{type: 'Competitor', id: `compare-${projectId}`},
				'Subscription',
			],
		}),
		getCompetitorCompare: builder.query({
			query: (projectId) => ({url: `competitor/projects/${projectId}/compare`, method: 'get'}),
			transformResponse: (body) => body?.data || {rows: []},
			providesTags: (result, error, projectId) => [{type: 'Competitor', id: `compare-${projectId}`}],
		}),
		getCompetitorReport: builder.query({
			query: ({projectId, reportId}) => ({
				url: `competitor/projects/${projectId}/reports/${reportId}`,
				method: 'get',
			}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, {reportId}) => [{type: 'Competitor', id: `report-${reportId}`}],
		}),
		// So trang mình với nhiều trang đối thủ — mỗi trang đối thủ live fetch = 1 url_checks
		// (cache 24h miễn phí); ghi chung keyword_page_terms → invalidate tag pages của Keyword.
		competitorContentGap: builder.mutation({
			query: ({projectId, own_url, competitor_ids, targets, min_competitors, refresh}) => ({
				url: `competitor/projects/${projectId}/content-gap`,
				method: 'post',
				data: {
					own_url,
					competitor_ids,
					...(targets ? {targets} : {}),
					...(min_competitors ? {min_competitors} : {}),
					...(refresh ? {refresh: 1} : {}),
				},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Keyword', id: `pages-${projectId}`},
				{type: 'Competitor', id: `coverage-${projectId}`},
				'Subscription',
			],
		}),
		// ===== Organic tuyệt đối qua DataForSEO Labs (§8 bước 4) =====
		// Trạng thái tab: own + đối thủ kèm overview đã cache (0đ) + quota Labs.
		getCompetitorLabs: builder.query({
			query: (projectId) => ({url: `competitor/projects/${projectId}/labs`, method: 'get'}),
			transformResponse: (body) => body?.data || {available: false, domains: [], quota: {}},
			providesTags: (result, error, projectId) => [{type: 'Competitor', id: `labs-${projectId}`}],
		}),
		// Phân tích sâu 1 domain (overview + top từ khoá) — miss trừ 1 lượt → Subscription.
		analyzeCompetitorLabs: builder.mutation({
			query: ({projectId, competitorId}) => ({
				url: `competitor/projects/${projectId}/labs/analyze`,
				method: 'post',
				data: {competitor_id: competitorId},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Competitor', id: `labs-${projectId}`},
				'Subscription',
			],
		}),
		// Top từ khoá organic đã cache của 1 domain (0đ).
		getCompetitorLabsKeywords: builder.query({
			query: ({projectId, competitorId}) => ({
				url: `competitor/projects/${projectId}/labs/keywords`,
				method: 'get',
				params: {competitor_id: competitorId},
			}),
			transformResponse: (body) => body?.data || {has_data: false},
		}),
		// Keyword gap thật (domain_intersection) — miss trừ 1 lượt.
		competitorLabsGap: builder.mutation({
			query: ({projectId, competitorId}) => ({
				url: `competitor/projects/${projectId}/labs/gap`,
				method: 'post',
				data: {competitor_id: competitorId},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: ['Subscription'],
		}),
		// Gợi ý đối thủ organic (competitors_domain) — miss trừ 1 lượt.
		competitorLabsSuggest: builder.mutation({
			query: (projectId) => ({url: `competitor/projects/${projectId}/labs/suggest`, method: 'post'}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: ['Subscription'],
		}),
		getCompetitorCoverage: builder.query({
			query: ({projectId, days, limit}) => ({
				url: `competitor/projects/${projectId}/coverage`,
				method: 'get',
				...(days || limit ? {params: {...(days ? {days} : {}), ...(limit ? {limit} : {})}} : {}),
			}),
			transformResponse: (body) => body?.data || {connected: false},
			providesTags: (result, error, {projectId}) => [{type: 'Competitor', id: `coverage-${projectId}`}],
		}),
	}),
});

export const {
	useGetCompetitorsQuery,
	useAddCompetitorMutation,
	useUpdateCompetitorMutation,
	useDeleteCompetitorMutation,
	useAnalyzeCompetitorMutation,
	useAnalyzeOwnSiteMutation,
	useGetCompetitorCompareQuery,
	useGetCompetitorReportQuery,
	useCompetitorContentGapMutation,
	useGetCompetitorCoverageQuery,
	useGetCompetitorLabsQuery,
	useAnalyzeCompetitorLabsMutation,
	useGetCompetitorLabsKeywordsQuery,
	useCompetitorLabsGapMutation,
	useCompetitorLabsSuggestMutation,
} = competitorApiSlice;
